import { PokerChipValue } from './PokerChipValue';

const chipValues: PokerChipValue[] = [
  PokerChipValue.TWENTY_FIVE_THOUSAND,
  PokerChipValue.FIVE_THOUSAND,
  PokerChipValue.ONE_THOUSAND,
  PokerChipValue.FIVE_HUNDRED,
  PokerChipValue.ONE_HUNDRED,
  PokerChipValue.TWENTY_FIVE,
  PokerChipValue.FIVE,
  PokerChipValue.ONE,
  PokerChipValue.TWENTY_FIVE_CENT,
  PokerChipValue.FIVE_CENT,
  PokerChipValue.ONE_CENT,
];

export function divideChipsIntoStacks(chips: number[], maxStacks = 7): number[][] {
  const stacks: number[][] = [];

  // Sort the chips in descending order
  const sortedChips = chips.sort((a, b) => b - a);

  // Create pointers for each stack
  const pointers: number[] = [];
  let currentValue = sortedChips[0];
  pointers.push(0);

  for (let i = 1; i < sortedChips.length; i++) {
    if (sortedChips[i] !== currentValue) {
      currentValue = sortedChips[i];
      pointers.push(i);
    }
  }

  // Add a final pointer at the end of the array
  pointers.push(sortedChips.length);

  // Distribute the chips into stacks based on the pointers
  for (let i = 0; i < pointers.length - 1; i++) {
    const startIndex = pointers[i];
    const endIndex = pointers[i + 1];
    const stack = sortedChips.slice(startIndex, endIndex);

    // Split stacks that exceed the maximum height limit into two equal parts
    if (stack.length > 8) {
      const halfLength = Math.floor(stack.length / 2);
      const newStack1 = stack.slice(0, halfLength);
      const newStack2 = stack.slice(halfLength);
      stacks.push(newStack1, newStack2);
    } else {
      stacks.push(stack);
    }
  }
  // If the number of stacks exceeds maxStacks, merge the smallest stacks
  while (stacks.length > maxStacks) {
    let smallestStackIndex = 0;
    let smallestStackSize = stacks[0].length;

    for (let i = 1; i < stacks.length; i++) {
      if (stacks[i].length < smallestStackSize) {
        smallestStackIndex = i;
        smallestStackSize = stacks[i].length;
      }
    }

    const smallestStack = stacks.splice(smallestStackIndex, 1)[0];
    if (smallestStackIndex === 0) {
      stacks[0] = smallestStack.concat(stacks[0]);
    } else {
      stacks[smallestStackIndex - 1] = stacks[smallestStackIndex - 1].concat(smallestStack);
    }
  }

  return stacks;
}

export function getChipValueInCents(chip: PokerChipValue): number {
  switch (chip) {
    case PokerChipValue.ONE_CENT:
      return 1;
    case PokerChipValue.FIVE_CENT:
      return 5;
    case PokerChipValue.TWENTY_FIVE_CENT:
      return 25;
    case PokerChipValue.ONE:
      return 100;
    case PokerChipValue.FIVE:
      return 500;
    case PokerChipValue.TWENTY_FIVE:
      return 2500;
    case PokerChipValue.ONE_HUNDRED:
      return 10000;
    case PokerChipValue.FIVE_HUNDRED:
      return 50000;
    case PokerChipValue.ONE_THOUSAND:
      return 100000;
    case PokerChipValue.FIVE_THOUSAND:
      return 500000;
    case PokerChipValue.TWENTY_FIVE_THOUSAND:
      return 2500000;
    default:
      throw new Error(`Invalid chip value: ${chip}`);
  }
}

export function chooseChips(amount: number, entropy = 0): PokerChipValue[] {
  const initialCents = Math.round(amount * 100);

  if (entropy === 0) {
    return chipValues.reduce(
      (acc, chip) => {
        const chipValueInCents = getChipValueInCents(chip);
        while (acc.remainingCents >= chipValueInCents) {
          acc.chips.push(chip);
          acc.remainingCents -= chipValueInCents;
        }
        return acc;
      },
      { chips: [] as PokerChipValue[], remainingCents: initialCents },
    ).chips;
  } else {
    return Array.from({ length: initialCents }).reduce<{ chips: PokerChipValue[]; remainingCents: number }>(
      (acc, _) => {
        if (acc.remainingCents > 0) {
          const availableChips = chipValues.filter((chip) => getChipValueInCents(chip) <= acc.remainingCents);
          const chip =
            Math.random() < entropy / 100
              ? availableChips[Math.floor(Math.random() * availableChips.length)]
              : availableChips[availableChips.length - 1];
          acc.chips.push(chip);
          acc.remainingCents -= getChipValueInCents(chip);
        }
        return acc;
      },
      { chips: [], remainingCents: initialCents },
    ).chips;
  }
}
